<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { qrSvg } from '../lib/qr.ts';

  /**
   * A4 label sheet.
   *
   * Plain paper and a grid, cut by hand — no dependency on a particular consumable, works on
   * any office printer. Geometry is expressed in millimetres against the real page box so
   * what appears on paper matches the preview.
   */
  let cols = $state(4);
  let scope = $state<'all' | 'range'>('all');
  let from = $state(1);
  let to = $state(50);
  let rendering = $state(false);
  let cells = $state<{ code: string; name: string; svg: string }[]>([]);

  const serial = (code: string) => Number(code.split('-')[1] ?? '0');

  const selection = $derived.by(() => {
    // Carts only. Sites are chosen from a list, so a site QR would never be scanned.
    const source = store.carts.filter((c) => c.status === 'active');
    const picked =
      scope === 'all'
        ? source
        : source.filter((e) => {
            const n = serial(e.code);
            return n >= Math.min(from, to) && n <= Math.max(from, to);
          });
    return picked.map((e) => ({ code: e.code, name: e.name }));
  });

  /** Usable A4 width after the 8mm @page margin, minus the inter-cell gaps. */
  const cellWidthMm = $derived((210 - 16 - 2 * (cols - 1)) / cols);
  const cellHeightMm = $derived(Math.round(cellWidthMm * 0.82 * 10) / 10);
  const perSheet = $derived(cols * Math.floor((297 - 16) / (cellHeightMm + 2)));

  async function render() {
    rendering = true;
    try {
      cells = await Promise.all(
        selection.map(async (e) => ({ ...e, svg: await qrSvg(e.code) })),
      );
    } finally {
      rendering = false;
    }
  }

  $effect(() => {
    // Re-render whenever the selection changes, so the preview is never stale.
    void selection;
    void render();
  });
</script>

<div class="no-print">
  <h1>Étiquettes</h1>
  <p class="small subtle" style="margin-bottom:.9rem">Une étiquette par chariot, à coller sur le chariot.</p>
  <div class="card">
    <div class="row wrap">
      <label class="field grow" style="margin:0">
        <span>Colonnes par page</span>
        <select bind:value={cols}>
          {#each [2, 3, 4, 5] as n (n)}<option value={n}>{n}</option>{/each}
        </select>
      </label>
      <label class="field grow" style="margin:0">
        <span>Sélection</span>
        <select bind:value={scope}>
          <option value="all">Tous ceux en service</option>
          <option value="range">Plage de numéros</option>
        </select>
      </label>
    </div>

    {#if scope === 'range'}
      <div class="row" style="margin-top:.5rem">
        <label class="field grow" style="margin:0"><span>De</span><input type="number" min="1" bind:value={from} /></label>
        <label class="field grow" style="margin:0"><span>À</span><input type="number" min="1" bind:value={to} /></label>
      </div>
    {/if}

    <p class="small muted" style="margin:.7rem 0 0">
      {selection.length} étiquette(s) · {cellWidthMm.toFixed(0)} × {cellHeightMm.toFixed(0)} mm ·
      environ {perSheet} par feuille A4 · {Math.ceil(selection.length / Math.max(perSheet, 1))} feuille(s)
    </p>

    <div class="row" style="margin-top:.7rem">
      <button class="primary grow" onclick={() => window.print()} disabled={rendering || cells.length === 0}>
        {#if rendering}<span class="spinner"></span>{/if} Imprimer
      </button>
    </div>
    <p class="tiny muted" style="margin:.5rem 0 0">
      Vérifiez dans l'aperçu du navigateur que l'échelle est à 100 % et les marges à « par
      défaut » — une mise à l'échelle automatique fausserait les dimensions.
    </p>
  </div>
</div>

{#if cells.length > 0}
  <div class="sheet-preview" style="grid-template-columns:repeat({cols},1fr)">
    {#each cells as c (c.code)}
      <div class="label-cell" style="height:{cellHeightMm}mm">
        {@html c.svg}
        <span class="code">{c.code}</span>
        <span class="name">{c.name}</span>
      </div>
    {/each}
  </div>
{:else if !rendering}
  <p class="muted small no-print">Rien à imprimer pour cette sélection.</p>
{/if}
